import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';
import { validateName, validateWhatsApp, formatWhatsApp, sanitizeFormData } from '@/utils/formValidation';
import { saveLead } from '@/utils/saveLead';
import { getCachedWhatsAppSettings } from '@/utils/whatsapp/whatsAppSettings';
import { createWhatsAppLink, createWhatsAppLinkFromSettings } from '@/utils/whatsapp/createWhatsAppLink';
import { trackWhatsAppClick, trackFormSubmission, getCurrentUTMParams } from '@/utils/googleAds';
import { supabase } from '@/integrations/supabase/client';

interface WhatsAppLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: string;
  /** Optional message that overrides settings.form_message for this submission. */
  customMessage?: string;
}

const WhatsAppLeadModal: React.FC<WhatsAppLeadModalProps> = ({ open, onOpenChange, source, customMessage }) => {
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; whatsapp?: string }>({});

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWhatsapp(formatWhatsApp(e.target.value));
  };

  const handleBlur = (field: 'name' | 'whatsapp') => {
    const validation = field === 'name' ? validateName(name) : validateWhatsApp(whatsapp);
    setFieldErrors(prev => ({
      ...prev,
      [field]: validation.isValid ? undefined : validation.errors[0]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const nameVal = validateName(name);
    const whatsVal = validateWhatsApp(whatsapp);

    if (!nameVal.isValid || !whatsVal.isValid) {
      setFieldErrors({
        name: nameVal.isValid ? undefined : nameVal.errors[0],
        whatsapp: whatsVal.isValid ? undefined : whatsVal.errors[0],
      });
      return;
    }

    setIsSubmitting(true);

    const sanitized = sanitizeFormData({ name, whatsapp });

    // 1. Build WhatsApp URL synchronously (from cache/defaults)
    const settings = getCachedWhatsAppSettings();
    const url = customMessage
      ? createWhatsAppLink(settings.whatsapp_number, customMessage)
      : createWhatsAppLinkFromSettings(settings, '', false);

    // 2. Open WhatsApp IMMEDIATELY in the user's click tick (avoids popup blocker)
    window.open(url, '_blank');

    // 3. Close modal and reset fields
    onOpenChange(false);
    setName('');
    setWhatsapp('');
    setFieldErrors({});
    setIsSubmitting(false);

    // 4. Background: save lead, tracking, DB insert (fire-and-forget)
    const utmParams = getCurrentUTMParams();
    trackWhatsAppClick(source, utmParams);
    trackFormSubmission({ name: sanitized.name, whatsapp: sanitized.whatsapp }, utmParams);

    saveLead({
      name: sanitized.name,
      whatsapp: sanitized.whatsapp,
      email: '',
      city: '',
      state: '',
      cnpj: '',
      taxRegime: 'Não informado',
      emitsInvoice: false,
      monthlyRevenue: 0,
    }, false).catch(err => console.error('Erro ao salvar lead:', err));

    supabase.from('whatsapp_clicks').insert({ source })
      .then(({ error }) => {
        if (error) console.error('Erro ao trackear clique WhatsApp:', error);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Fale com nosso time no WhatsApp!</DialogTitle>
          <DialogDescription>
            Preencha seus dados para iniciar a conversa com um especialista.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="modal-name">Nome completo</Label>
            <Input
              id="modal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => handleBlur('name')}
              placeholder="Digite seu nome completo"
              required
              autoComplete="name"
              className={`min-h-[44px] ${fieldErrors.name ? 'border-red-500 focus:border-red-500' : ''}`}
            />
            {fieldErrors.name && <p className="text-sm text-red-600">{fieldErrors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="modal-whatsapp">WhatsApp</Label>
            <Input
              id="modal-whatsapp"
              value={whatsapp}
              onChange={handleWhatsAppChange}
              onBlur={() => handleBlur('whatsapp')}
              placeholder="(11) 99999-9999"
              required
              autoComplete="tel"
              maxLength={15}
              className={`min-h-[44px] ${fieldErrors.whatsapp ? 'border-red-500 focus:border-red-500' : ''}`}
            />
            {fieldErrors.whatsapp && <p className="text-sm text-red-600">{fieldErrors.whatsapp}</p>}
          </div>

          <Button
            type="submit"
            disabled={
              isSubmitting ||
              !validateName(name).isValid ||
              !validateWhatsApp(whatsapp).isValid
            }
            className="w-full min-h-[48px] bg-[hsl(142,72%,45%)] hover:bg-[hsl(142,72%,38%)] text-white font-semibold text-base"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            {isSubmitting ? 'Processando...' : 'Enviar Mensagem'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppLeadModal;
